import { useEffect, useState } from "react";

import { Box, Button, Checkbox, CheckboxGroup, Divider, Flex, FormControl, FormErrorMessage, FormLabel, HStack, Icon, Input, Text, Textarea, useDisclosure, useToast } from "@chakra-ui/react";
import { Field, Form, Formik } from "formik";
import { useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";
import { FaEye } from "react-icons/fa";

import ArrowIcon from "@/assets/ArrowIcon";
import { DURATION_TOAST, ROLES, COLORS, REGISTRATION_STATUS } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import SelectCountry from "@/components/Selects/SelectCountry";
import SelectReviewer from "@/components/Selects/SelectReviewer";
import FileUploader from "@/components/Fields/FileUploader";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { companyProfileSchema } from "@/schemas/companyProfileSchema";
import { getInspectorById, putInspector } from "@/services/inspectorService";
import { downloadFile } from "@/services/downloadService";
import { getShipOwnerById, putShipOwner } from "@/services/shipOwnerService";

const CompanyProfile = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const appId = useAppSelector((state: RootState) => state.account.appId);
    const role = useAppSelector((state: RootState) => state.account.roleName);

    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");
    const [dataAdd, setDataAdd] = useState<any>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [dataDetail, setDataDetail] = useState<any>(null);

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel to Edit Company Profile?");
        setConfirmationTest("Are you sure you want to cancel?");
        setConfirmationTitleButton("Yes");
    }

    const handleSubmit = async (data: any) => {
        setTitle("Save Company Profile?");
        setConfirmationTest("Are you sure you want to edit?");
        setConfirmationTitleButton("Save");
        onOpen();
        setDataAdd(data);
        setIsSubmitting(true);
    }

    const generateDataEditInspectingCompany = () => {
        const data = new FormData();
        data.append('InspectorId', appId);
        data.append('Sirebire', dataAdd.type.includes('SIRE | BIRE'));
        data.append('Vetting', dataAdd.type.includes('Vetting'));
        data.append('isReviewer', dataAdd.reviewer);
        data.append('CompanyName', dataAdd.companyName);
        data.append('Address', dataAdd.address.trim());
        data.append('Country', dataAdd.country);
        data.append('ContactPerson', dataAdd.contactPerson);
        data.append('Email', dataAdd.email);
        data.append('AlternateEmail', dataAdd.alternateEmail);
        data.append('Phone', dataAdd.phone);
        data.append('DGT1File', dataAdd.dgt1);
        data.append('CORFile', dataAdd.cor);
        data.append('CompanyProfileFile', dataAdd.companyProfile);
        data.append('FinanceReportsFile', dataAdd.lapKeuAkhir);
        data.append('BankDetailFile', dataAdd.bankDetails);
        data.append('NIBBusRegFile', dataAdd.NIB);
        data.append('RegisterStatus', String(REGISTRATION_STATUS.IN_REVIEW));
        return data;
    }

    const generateDataEditShipOperator = () => {
        const data = new FormData();
        data.append('ShipOwnerId', appId);
        data.append('Sirebire', dataAdd.type.includes('SIRE | BIRE'));
        data.append('Vetting', dataAdd.type.includes('Vetting'));
        data.append('OnBehalfCompany', dataAdd.companyName);
        data.append('Address', dataAdd.address.trim());
        data.append('Country', dataAdd.country);
        data.append('ContactPerson', dataAdd.contactPerson);
        data.append('Email', dataAdd.email);
        data.append('AlternateEmail', dataAdd.alternateEmail);
        data.append('Phone', dataAdd.phone);
        data.append('DocumentFile', dataAdd.doc || '');
        data.append('RegisterStatus', String(REGISTRATION_STATUS.IN_REVIEW));
        return data;
    }

    const handleConfirm = async () => {
        onClose();

        if (title === "Cancel to Edit Company Profile?") {
            navigate('/settings/company-profile');
            return;
        }

        let obj: FormData;
        let response;
        if (role === ROLES.INSPECTING_COMPANY){
            obj = generateDataEditInspectingCompany();
            response = await putInspector(obj);
        } else if (role === ROLES.SHIP_OPERATOR) {
            obj = generateDataEditShipOperator();
            response = await putShipOwner(obj);
        } else {
            response = Promise.reject("User is not inspecting company or ship operator.")
        }
        setIsSubmitting(false);
        if (response.status === HttpStatusCode.Ok) {
            toast({
                title: 'Profile updated.',
                description: "We've updated company profile for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                navigate('/settings/company-profile')
            }, 1000);
        } else {
            toast({
                title: 'Company profile failed to update.',
                description: "We unabled to update company profile for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleDrop = (acceptedFiles: File[], setFieldValue: any, fieldName: string) => {
        setFieldValue(fieldName, acceptedFiles[0]);
    };

    const getDataDetailInspecting = async () => {
        const response = await getInspectorById(appId) as any;
        if (response?.status === HttpStatusCode.Forbidden) {
            toast({
                title: response?.status,
                description: response?.message,
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            return;
        }

        if (response) {
            setDataDetail(response.data);
        }
    }

    const getDataDetailShipOperator = async () => {
        const response = await getShipOwnerById(appId) as any;

        if (response?.status === HttpStatusCode.Forbidden) {
            toast({
                title: response?.status,
                description: response?.message,
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            return;
        }

        if (response) {
            setDataDetail(response.data);
        }
    }

	const handleDownload = async (id: string) => {
		await downloadFile(id);
    }

    useEffect(() => {
        if (appId) {
            if (role === ROLES.INSPECTING_COMPANY) {
                getDataDetailInspecting();
            } else if (role === ROLES.SHIP_OPERATOR) {
                getDataDetailShipOperator();
            }
        }
    }, [appId])

    return (
        <>
            <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
            <div className="form-container">
                <Formik
                    initialValues={{
                        type: dataDetail ? [dataDetail?.sirebire ? 'SIRE | BIRE' : '', dataDetail?.vetting ? 'Vetting' : ''] : [],
                        companyName: role === ROLES.INSPECTING_COMPANY ? dataDetail?.companyName : dataDetail?.onBehalfCompany,
                        reviewer: dataDetail?.isReviewer ? 'true' : 'false',
                        address: dataDetail?.address ?? '',
                        country: dataDetail?.country ?? '',
                        contactPerson: dataDetail?.contactPerson ?? '',
                        email: dataDetail?.email ?? '',
                        alternateEmail: dataDetail?.alternateEmail ?? '',
                        phone: dataDetail?.phone ?? '',
                        status: !dataDetail?.status
                    }}
                    validationSchema={() => companyProfileSchema(role)}
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}
                    enableReinitialize={true}
                >
                    {(props) => (
                        <Form style={{ marginTop: 20 }}>
                            {(role === ROLES.INSPECTING_COMPANY) && <Field name='type'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.type && form.touched.type} marginBottom={4}>
                                        <div className="form-field-container">
                                            <FormLabel htmlFor="type" className="form-label">Type<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <CheckboxGroup
                                                    value={props.values.type}
                                                    onChange={(value: string[]) => props.setFieldValue('type', value)}
                                                    isDisabled={dataDetail?.status === 2}
                                                >
                                                    <Flex gap={8}>
                                                        <Checkbox value="SIRE | BIRE">SIRE | BIRE</Checkbox>
                                                        <Checkbox value="Vetting">Vetting</Checkbox>
                                                    </Flex>
                                                </CheckboxGroup>
                                                <FormErrorMessage>{form.errors.type}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>}
                            <Field name='companyName'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.companyName && form.touched.companyName} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Company Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Company Name' className="form-input" disabled />
                                                <FormErrorMessage>{form.errors.companyName}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            {(role === ROLES.INSPECTING_COMPANY) && <Field name='reviewer'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.reviewer && form.touched.reviewer} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">SIRE Review<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <SelectReviewer name='reviewer' handleChange={({ value }: any) => props.setFieldValue('reviewer', value)} value={props.values.reviewer} hasError={form.errors.reviewer && form.touched.reviewer} defaultValue={props.values.reviewer} isDisabled={role !== 'super admin'} />
                                                <FormErrorMessage>{form.errors.reviewer}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>}
                            <Field name='address'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.address && form.touched.address} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Address<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Textarea {...field} placeholder='Address' className="form-input" />
                                                <FormErrorMessage>{form.errors.address}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='country'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.country && form.touched.country} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Country<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <SelectCountry name='country' handleChange={({ value }: any) => props.setFieldValue('country', value)} value={props.values.country} hasError={form.errors.country && form.touched.country} defaultValue={props.values.country} />
                                                <FormErrorMessage>{form.errors.country}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='contactPerson'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.contactPerson && form.touched.contactPerson} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Contact Person<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Contact Person' className="form-input" />
                                                <FormErrorMessage>{form.errors.contactPerson}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='email'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.email && form.touched.email} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Email<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Email' className="form-input" disabled />
                                                <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='alternateEmail'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.alternateEmail && form.touched.alternateEmail} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Alternative Email</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Alternative Email' className="form-input" />
                                                <FormErrorMessage>{form.errors.alternateEmail}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='phone'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.phone && form.touched.phone} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Phone Number</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Phone Number' className="form-input" />
                                                <FormErrorMessage>{form.errors.phone}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>

                            {role === ROLES.SHIP_OPERATOR &&
                                <Field name='doc'>
                                    {({ form }: any) => (
                                        <FormControl isInvalid={form.errors.doc && form.submitCount} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Doc<span style={{ color: 'red' }}>*</span></FormLabel>
                                                <div className="form-input-container">
                                                    {dataDetail?.documents && <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <Box
                                                            borderWidth="1px"
                                                            borderRadius="md"
                                                            padding={4}
                                                            backgroundColor="green.50"
                                                            borderColor="green.200"
                                                            width="100%"
                                                            marginBottom="15px"
                                                        >
                                                            <HStack
                                                                borderWidth="1px"
                                                                borderRadius="md"
                                                                padding={3}
                                                                backgroundColor="white"
                                                                justifyContent="space-between"
                                                                width="100%"
                                                            >
                                                                <HStack>
                                                                    <Text>DOC File</Text>
                                                                </HStack>
																	<HStack cursor='pointer' onClick={() => handleDownload(dataDetail.documents)}>
                                                                    <Icon as={FaEye} />
                                                                    <Text>Download Document</Text>
                                                                </HStack>
                                                            </HStack>
                                                        </Box>
                                                        {/* <EditIcon
                                                            style={{ marginLeft: '10px', marginTop: '-20px', cursor: 'pointer' }}
                                                            onClick={() => handleShowEditImage('doc')}
                                                        /> */}
                                                    </div>}

                                                    {(!dataDetail?.documents) &&
                                                        <div style={{ width: '95%' }}>
                                                            <FileUploader
                                                                onDrop={(e) => props.setFieldValue('doc', e[0])}
                                                                isInvalid={form.errors.NIB && form.submitCount}
                                                                type="registration-inspectingcompany"
                                                                isDisabled={props.isSubmitting}
                                                                isPadding={false}
                                                            />
                                                        </div>
                                                    }
                                                    <FormErrorMessage>{form.errors.doc}</FormErrorMessage>
                                                </div>
                                            </div>
                                            
                                        </FormControl>
                                    )}
                                </Field>
                            }

                            {role === 'inspecting company' &&
                                <>
                                    <Field name='dgt1'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.dgt1 && form.submitCount} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">DGT-1<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        {dataDetail?.dgT1 && <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <Box
                                                                borderWidth="1px"
                                                                borderRadius="md"
                                                                padding={4}
                                                                backgroundColor="green.50"
                                                                borderColor="green.200"
                                                                width="100%"
                                                                marginBottom="15px"
                                                            >
                                                                <HStack
                                                                    borderWidth="1px"
                                                                    borderRadius="md"
                                                                    padding={3}
                                                                    backgroundColor="white"
                                                                    justifyContent="space-between"
                                                                    width="100%"
                                                                >
                                                                    <HStack>
                                                                        <Text>DGT-1 File</Text>
                                                                    </HStack>
																	<HStack cursor='pointer' onClick={() => handleDownload(dataDetail.dgT1)}>
                                                                        <Icon as={FaEye} />
                                                                        <Text>Download Document</Text>
                                                                    </HStack>
                                                                </HStack>
                                                            </Box>
                                                            {/* <EditIcon
                                                                style={{ marginLeft: '10px', marginTop: '-20px', cursor: 'pointer' }}
                                                                onClick={() => handleShowEditImage('dgt1')}
                                                            /> */}
                                                        </div>}

                                                        {/* {(!dataDetail?.dgT1 || editableDocs.includes('dgt1')) &&
                                                            <div style={{ width: '95%' }}>
                                                                <FileUploader
                                                                    onDrop={(e) => handleDrop(e, props.setFieldValue, 'dgt1')}
                                                                    isInvalid={form.errors.dgt1 && form.submitCount}
                                                                    type="registration-inspectingcompany"
                                                                    isDisabled={props.isSubmitting}
                                                                    isPadding={false}
                                                                />
                                                            </div>
                                                        } */}
                                                        <FormErrorMessage>{form.errors.dgt1}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                        )}
                                    </Field>

                                    <Field name='cor'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.cor && form.submitCount} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">COR<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        {dataDetail?.cor && <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <Box
                                                                borderWidth="1px"
                                                                borderRadius="md"
                                                                padding={4}
                                                                backgroundColor="green.50"
                                                                borderColor="green.200"
                                                                width="100%"
                                                                marginBottom="15px"
                                                            >
                                                                <HStack
                                                                    borderWidth="1px"
                                                                    borderRadius="md"
                                                                    padding={3}
                                                                    backgroundColor="white"
                                                                    justifyContent="space-between"
                                                                    width="100%"
                                                                >
                                                                    <HStack>
                                                                        <Text>COR File</Text>
                                                                    </HStack>
																	<HStack cursor='pointer' onClick={() => handleDownload(dataDetail.cor)}>
                                                                        <Icon as={FaEye} />
                                                                        <Text>Download Document</Text>
                                                                    </HStack>
                                                                </HStack>
                                                            </Box>
                                                            {/* <EditIcon
                                                                style={{ marginLeft: '10px', marginTop: '-20px', cursor: 'pointer' }}
                                                                onClick={() => handleShowEditImage('cor')}
                                                            /> */}
                                                        </div>}

                                                        {(!dataDetail?.cor) &&
                                                            <div style={{ width: '95%' }}>
                                                                <FileUploader
                                                                    onDrop={(e) => handleDrop(e, props.setFieldValue, 'cor')}
                                                                    isInvalid={form.errors.cor && form.submitCount}
                                                                    type="registration-inspectingcompany"
                                                                    isDisabled={props.isSubmitting}
                                                                    isPadding={false}
                                                                />
                                                            </div>
                                                        }
                                                        <FormErrorMessage>{form.errors.cor}</FormErrorMessage>
                                                    </div>
                                                </div>
                                                
                                            </FormControl>
                                        )}
                                    </Field>

                                    <Field name='companyProfile'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.companyProfile && form.submitCount} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Company Profile</FormLabel>
                                                    <div className="form-input-container">
                                                        {dataDetail?.companyProfile && <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <Box
                                                                borderWidth="1px"
                                                                borderRadius="md"
                                                                padding={4}
                                                                backgroundColor="green.50"
                                                                borderColor="green.200"
                                                                width="100%"
                                                                marginBottom="15px"
                                                            >
                                                                <HStack
                                                                    borderWidth="1px"
                                                                    borderRadius="md"
                                                                    padding={3}
                                                                    backgroundColor="white"
                                                                    justifyContent="space-between"
                                                                    width="100%"
                                                                >
                                                                    <HStack>
                                                                        <Text>Company Profile File</Text>
                                                                    </HStack>
																	<HStack cursor='pointer' onClick={() => handleDownload(dataDetail.companyProfile)}>
                                                                        <Icon as={FaEye} />
                                                                        <Text>Download Document</Text>
                                                                    </HStack>
                                                                </HStack>
                                                            </Box>
                                                            {/* <EditIcon
                                                                style={{ marginLeft: '10px', marginTop: '-20px', cursor: 'pointer' }}
                                                                onClick={() => handleShowEditImage('companyProfile')}
                                                            /> */}
                                                        </div>}

                                                        {(!dataDetail?.companyProfile) &&
                                                            <div style={{ width: '95%' }}>
                                                                <FileUploader
                                                                    onDrop={(e) => handleDrop(e, props.setFieldValue, 'companyProfile')}
                                                                    isInvalid={form.errors.companyProfile && form.submitCount}
                                                                    type="registration-inspectingcompany"
                                                                    isDisabled={props.isSubmitting}
                                                                    isPadding={false}
                                                                />
                                                            </div>
                                                        }
                                                        <FormErrorMessage>{form.errors.companyProfile}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>

                                    <Field name='latestFinancialReport'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.latestFinancialReport && form.submitCount} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Latest Financial Report</FormLabel>
                                                    <div className="form-input-container">
                                                        {dataDetail?.financeReports && <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <Box
                                                                borderWidth="1px"
                                                                borderRadius="md"
                                                                padding={4}
                                                                backgroundColor="green.50"
                                                                borderColor="green.200"
                                                                width="100%"
                                                                marginBottom="15px"
                                                            >
                                                                <HStack
                                                                    borderWidth="1px"
                                                                    borderRadius="md"
                                                                    padding={3}
                                                                    backgroundColor="white"
                                                                    justifyContent="space-between"
                                                                    width="100%"
                                                                >
                                                                    <HStack>
                                                                        <Text>Latest Financial Report File</Text>
                                                                    </HStack>
																	<HStack cursor='pointer' onClick={() => handleDownload(dataDetail.latestFinancialReport)}>
                                                                        <Icon as={FaEye} />
                                                                        <Text>Download Document</Text>
                                                                    </HStack>
                                                                </HStack>
                                                            </Box>
                                                            {/* <EditIcon
                                                                style={{ marginLeft: '10px', marginTop: '-20px', cursor: 'pointer' }}
                                                                onClick={() => handleShowEditImage('latestFinancialReport')}
                                                            /> */}
                                                        </div>}

                                                        {(!dataDetail?.financeReports) &&
                                                            <div style={{ width: '95%' }}>
                                                                <FileUploader
                                                                    onDrop={(e) => handleDrop(e, props.setFieldValue, 'latestFinancialReport')}
                                                                    isInvalid={form.errors.latestFinancialReport && form.submitCount}
                                                                    type="registration-inspectingcompany"
                                                                    isDisabled={props.isSubmitting}
                                                                    isPadding={false}
                                                                />
                                                            </div>
                                                        }
                                                        <FormErrorMessage>{form.errors.latestFinancialReport}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>

                                    <Field name='bankDetails'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.bankDetails && form.submitCount} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Bank Details</FormLabel>
                                                    <div className="form-input-container">
                                                        {dataDetail?.bankDetail && <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <Box
                                                                borderWidth="1px"
                                                                borderRadius="md"
                                                                padding={4}
                                                                backgroundColor="green.50"
                                                                borderColor="green.200"
                                                                width="100%"
                                                                marginBottom="15px"
                                                            >
                                                                <HStack
                                                                    borderWidth="1px"
                                                                    borderRadius="md"
                                                                    padding={3}
                                                                    backgroundColor="white"
                                                                    justifyContent="space-between"
                                                                    width="100%"
                                                                >
                                                                    <HStack>
                                                                        <Text>Bank Details File</Text>
                                                                    </HStack>
																	<HStack cursor='pointer' onClick={() => handleDownload(dataDetail.bankDetails)}>
                                                                        <Icon as={FaEye} />
                                                                        <Text>Download Document</Text>
                                                                    </HStack>
                                                                </HStack>
                                                            </Box>
                                                            {/* <EditIcon
                                                                style={{ marginLeft: '10px', marginTop: '-20px', cursor: 'pointer' }}
                                                                onClick={() => handleShowEditImage('bankDetails')}
                                                            /> */}
                                                        </div>}

                                                        {(!dataDetail?.bankDetail) &&
                                                            <div style={{ width: '95%' }}>
                                                                <FileUploader
                                                                    onDrop={(e) => handleDrop(e, props.setFieldValue, 'bankDetails')}
                                                                    isInvalid={form.errors.bankDetails && form.submitCount}
                                                                    type="registration-inspectingcompany"
                                                                    isDisabled={props.isSubmitting}
                                                                    isPadding={false}
                                                                />
                                                            </div>
                                                        }
                                                        <FormErrorMessage>{form.errors.bankDetails}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>

                                    <Field name='NIB'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.NIB && form.submitCount} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">NIB / Company Bussiness Registration</FormLabel>
                                                    <div className="form-input-container">
                                                        {dataDetail?.nibBusReg && <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <Box
                                                                borderWidth="1px"
                                                                borderRadius="md"
                                                                padding={4}
                                                                backgroundColor="green.50"
                                                                borderColor="green.200"
                                                                width="100%"
                                                                marginBottom="15px"
                                                            >
                                                                <HStack
                                                                    borderWidth="1px"
                                                                    borderRadius="md"
                                                                    padding={3}
                                                                    backgroundColor="white"
                                                                    justifyContent="space-between"
                                                                    width="100%"
                                                                >
                                                                    <HStack>
                                                                        <Text>NIB File</Text>
                                                                    </HStack>
																	<HStack cursor='pointer' onClick={() => handleDownload(dataDetail.NIB)}>
                                                                        <Icon as={FaEye} />
                                                                        <Text>Download Document</Text>
                                                                    </HStack>
                                                                </HStack>
                                                            </Box>
                                                            {/* <EditIcon
                                                                style={{ marginLeft: '10px', marginTop: '-20px', cursor: 'pointer' }}
                                                                onClick={() => handleShowEditImage('NIB')}
                                                            /> */}
                                                        </div>}

                                                        {(!dataDetail?.nibBusReg) &&
                                                            <div style={{ width: '95%' }}>
                                                                <FileUploader
                                                                    onDrop={(e) => handleDrop(e, props.setFieldValue, 'NIB')}
                                                                    isInvalid={form.errors.NIB && form.submitCount}
                                                                    type="registration-inspectingcompany"
                                                                    isDisabled={props.isSubmitting}
                                                                    isPadding={false}
                                                                />
                                                            </div>
                                                        }
                                                        <FormErrorMessage>{form.errors.NIB}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                </>
                            }

                            <Divider />
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
                                {role === 'super admin' && <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    isLoading={isSubmitting}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>}
                                <Button
                                    mt={4}
                                    bgColor={COLORS.BUTTON.BACKGROUND}
                                    color={COLORS.BUTTON.FOREGROUND}
                                    isLoading={isSubmitting}
                                    type='submit'
                                    rightIcon={<ArrowIcon />}
                                >
                                    Save Company Profile
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    )
}

export default CompanyProfile;
